import { toast } from 'react-toastify'

import { useContext } from 'react'

import { useRealtimeSubscription, useSetState } from '@domain-group/react-hooks'

import RealtimeContext from '@contexts/realtimeContext'

import useAttachment from '@hooks/useAttachment'
import useAttachments from '@hooks/useAttachments'
import useCarousel from '@hooks/useCarousel'
import useOrderItem from '@hooks/useOrderItem'
import useSelectEntities from '@hooks/useSelectEntities'

import type { AttachmentModel, OrderItemModel } from '@models/types'

type DefaultState = {
  bulkSelectMode: boolean,
  commentMode: boolean,
}

const defaultState: DefaultState = {
  bulkSelectMode: false,
  commentMode: false,
}

type SetState = (state: Partial<DefaultState>) => void

type ToggleOrSelectAttachmentParams = {
  selectedAttachment: AttachmentModel,
  currentAttachmentId: number,
  setState: SetState,
  shouldOpenCommentBox?: boolean,
}
const toggleOrSelectAttachment = (params: ToggleOrSelectAttachmentParams) => {
  const {
    selectedAttachment, currentAttachmentId, setState, shouldOpenCommentBox,
  } = params

  const reviewStatus = selectedAttachment?.data?.review_status ?? ''

  // toggles off the selected Attachment if clicked
  if (selectedAttachment.id === currentAttachmentId){
    setState({ commentMode: false })
    return
  }

  const showCommentBox = reviewStatus === 'rejected' || shouldOpenCommentBox
  setState({ commentMode: showCommentBox })
}

type AttachmentsPayload = {
  payload: {
    links: { original_url: string },
  },
}

type ProcessingCompleteParams = {
  data: AttachmentsPayload,
}

const processingComplete = (params: ProcessingCompleteParams) => {
  const { data } = params

  const link = data?.payload?.links?.original_url ?? ''

  if (link) window.open(link, '_blank')

  toast('Attachments Downloaded Successfully')
}

interface UseMediaReviewerOrderItemParams {
  orderItem: OrderItemModel,
}

const useMediaReviewerOrderItem = (params: UseMediaReviewerOrderItemParams) => {
  const { orderItem } = params

  const [state, setState] = useSetState(defaultState)
  const { bulkSelectMode, commentMode } = state

  const { service } = useOrderItem(orderItem)

  const attachmentsPayload = useAttachments({
    filters: {
      attachable_id: orderItem.id,
      attachable_type: 'OrderItem',
      attachment_type: 'deliverable',
    },
  })

  const {
    filteredAttachments,
    hasFilteredAttachments,
    loading,
  } = attachmentsPayload

  const carouselPayload = useCarousel({ items: filteredAttachments })
  const { callbacks: { setSelectedIndex }, selectedIndex } = carouselPayload

  // @ts-expect-error TS2538
  const attachmentPayload = useAttachment(filteredAttachments[selectedIndex])
  const {
    attachment: selectedAttachment,
    attachmentType,
    callbacks: {
      copyToCampaign,
      prepareDownload,
      updateAttachment,
    },
  } = attachmentPayload

  const unselectEntitiesPayload = useSelectEntities()
  const {
    callbacks: {
      deselectAll,
      selectAll,
      toggleId,
    },
    selectedEntityIds,
  } = unselectEntitiesPayload

  const realtimeContext = useContext(RealtimeContext)
  // @ts-expect-error TS2339
  const { clientRef } = realtimeContext

  useRealtimeSubscription({
    apiKey: process.env.REACT_APP_ABLY_PUBLIC_KEY,
    callbacks: {
      success: (data: AttachmentsPayload) => processingComplete({ data }),
    },
    channelName: 'orderItems',
    clientRef,
    eventName: `orderItem.${orderItem.id}.downloadPreparation.complete`,
  }, [orderItem.id])

  const allSelected = filteredAttachments.length === selectedEntityIds.length
  const hasBulkSelectedIds = selectedEntityIds.length !== 0

  return {
    allSelected,
    attachmentType,
    bulkSelectMode,
    callbacks: {
      copyToCampaign: (attachmentIds: number[]) => copyToCampaign({ attachmentIds, orderItemId: orderItem.id }),
      deselectAllEntityIds: () => deselectAll(),
      downloadAttachments: () => prepareDownload(orderItem.id, selectedEntityIds),
      selectAttachment: (index: number, shouldOpenCommentBox?: boolean) => {
        setSelectedIndex(index === selectedIndex ? undefined : index)
        toggleOrSelectAttachment({
          selectedAttachment: filteredAttachments[index],
          // @ts-expect-error TS2538
          currentAttachmentId: filteredAttachments[selectedIndex]?.id,
          setState,
          shouldOpenCommentBox,
        })
      },
      selectAllEntityIds: () => selectAll(filteredAttachments),
      setCommentMode: (values: Partial<typeof defaultState>) => setState(values),
      setModalState: (values: Partial<typeof defaultState>) => setState(values),
      toggleEntityId: (entityId: number) => toggleId(entityId),
      updateAttachment: (attachmentParams: Partial<AttachmentModel>) => updateAttachment(attachmentParams),
    },
    carouselPayload,
    commentMode,
    filteredAttachments,
    hasBulkSelectedIds,
    hasFilteredAttachments,
    loading,
    selectedAttachment,
    selectedAttachmentId: selectedAttachment?.id,
    selectedIndex,
    selectedEntityIds,
    service,
  }
}

export type UseMediaReviewerOrderItemPayload = ReturnType<typeof useMediaReviewerOrderItem>

export default useMediaReviewerOrderItem
